import React from 'react';
import { createPortal } from 'react-dom';
import { Modal, Button, Typography, Divider, theme } from 'antd';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { IconFileInvoice } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { formatFullDate, formatShortDateTime } from '../helpers/dateFormatHelper';
import { isDailyRentalMode, lastRentalDay } from '../helpers/rentalPeriodHelper';
import { computeRentalDays } from '../helpers/insuranceCostHelper';
import { latestQuoteRevision } from '../helpers/quoteHelper';
import QuoteCountdown, { QuoteCountdownVariant } from './QuoteCountdown';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT } from '../theme/creamyTokens';

const { Text, Title } = Typography;

interface BreakdownRow {
  key: string;
  label: string;
  detail?: string;
  amount: number;
}

interface QuoteBreakdownSheetProps {
  open: boolean;
  /** Null while the opener hasn't loaded the booking yet -- the sheet just doesn't render. */
  booking?: IBooking;
  /** Who's looking, so the revision history can say "you" vs "them". */
  userId?: string;
  onClose: () => void;
  /**
   * The decisions, rendered pinned at the bottom. Whichever the caller
   * omits simply isn't offered -- the host passes all three, the guest
   * passes accept only when there's a host revision to accept (see
   * MessageThreadPanel), and a read-only viewer passes none.
   */
  onAccept?: () => void;
  accepting?: boolean;
  onPropose?: () => void;
  onReject?: () => void;
  /**
   * The guest's own way out, and the reason this sheet can't be actionless.
   * Nothing in a reserva diferida is the guest's to accept until the host has
   * counter-offered, so a guest opening their own pending quote used to land
   * on a breakdown with an empty footer and no way forward -- this sheet is
   * the primary surface for that booking, so a dead end here reads as the
   * feature being broken. Cancelling is always theirs to do.
   */
  onCancel?: () => void;
  cancelling?: boolean;
  /**
   * Offered once the quote is agreed and only the money is missing
   * (AwaitingPayment). Takes precedence over the negotiation actions above,
   * which no longer apply at that point -- there is nothing left to accept
   * or counter, just a bill to settle.
   */
  onPay?: () => void;
}

/**
 * What a reserva diferida's number is actually made of, line by line.
 *
 * Exists because the host was being asked to accept, counter or reject a
 * single total with no way to inspect it: three decision buttons and one
 * figure. Nobody can price a job from that. Everything shown here already
 * travels on the booking itself (BookingController.ToResponse), so this
 * needed no new endpoint -- it was purely missing UI.
 *
 * Deliberately the same component for both sides. The host reads it to
 * decide; the guest reads it to see what they've been counter-offered and
 * why. A second, host-only variant would have drifted from this one the
 * first time either was touched, and the guest would have kept being shown
 * a total with no explanation.
 *
 * The revision history at the bottom is the part that makes a counter-offer
 * legible: "you asked 800, they proposed 950 because the transport is
 * longer than it looked" is a different conversation from "950".
 */
const QuoteBreakdownSheet: React.FC<QuoteBreakdownSheetProps> = ({
  open,
  booking,
  userId,
  onClose,
  onAccept,
  accepting = false,
  onPropose,
  onReject,
  onCancel,
  cancelling = false,
  onPay,
}) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const dragControls = useDragControls();

  if (!booking) return null;

  const currency = booking.hostCurrencyCode;
  const money = (value: number) => formatCurrency(value, currency, i18n.language);
  const daily = isDailyRentalMode(booking.rentalMode);
  const days = computeRentalDays(booking.startAt, booking.endAt);

  // The dates line reads differently per mode for the same reason the
  // checkout summary does: a daily rental's endAt is exclusive and naming
  // it would print a day nobody rents (see rentalPeriodHelper).
  const datesLabel = daily
    ? `${formatFullDate(dayjs(booking.startAt), i18n.language)} → ${formatFullDate(lastRentalDay(booking.endAt), i18n.language)}`
    : `${formatFullDate(dayjs(booking.startAt), i18n.language)}, ${dayjs(booking.startAt).format('HH:mm')} – ${dayjs(booking.endAt).format('HH:mm')}`;

  // Once a quote has been itemised, the live version's own lines ARE the
  // breakdown -- that is what the two parties are agreeing on, and it always
  // sums to the total by construction. The bucket-derived rows below are the
  // fallback for an immediate-pay booking, which never had a quote at all.
  const liveRevision = latestQuoteRevision(booking);
  const revisionRows: BreakdownRow[] | undefined = liveRevision?.lines?.length
    ? liveRevision.lines.map((line, index) => ({
        key: `revision-${index}`,
        label: line.title,
        // The arithmetic first, then the explanation. A line priced as so
        // many hours at so much an hour is the single thing a customer
        // actually checks, and it reads as a quote from a tradesperson
        // rather than as a figure somebody decided on.
        //
        // Never says where the price came from: whether the provider read it
        // off their own rate card or estimated it is their business, not the
        // customer's -- they are being given a price, not a confidence level.
        detail: [
          line.quantity != null && line.unitPrice != null
            ? `${line.quantity} ${line.unit ?? ''}`.trim() + ` × ${money(line.unitPrice)}`
            : undefined,
          line.description,
        ]
          .filter(Boolean)
          .join(' · ') || undefined,
        amount: line.amount,
      }))
    : undefined;

  const rows: BreakdownRow[] = [];
  rows.push({
    key: 'base',
    label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_BASE_PRICE_LABEL),
    // Only meaningful for the by-days mode -- an Hourly/DayPass base price
    // isn't a daily rate times a day count.
    detail: daily && days > 0 ? `${money(booking.basePrice / days)}/${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX)} × ${days} ${translate(t, days === 1 ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAYS_SUFFIX)}` : undefined,
    amount: booking.basePrice,
  });

  if (booking.operatorCost != null && booking.operatorCost > 0) {
    rows.push({
      key: 'operator',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_COST_LABEL),
      detail: booking.operatorName,
      amount: booking.operatorCost,
    });
  }

  if (booking.deliveryCost != null && booking.deliveryCost > 0) {
    const b = booking.deliveryCostBreakdown;
    rows.push({
      key: 'delivery',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_COST_LABEL),
      // Distance and legs are the two things a host disputes most often on a
      // transport line, so they're spelled out rather than folded into one
      // figure. legs === 2 means the return pickup was requested too.
      detail: b
        ? translate(t, b.legs === 2 ? TRANSLATIONS.QUOTE_BREAKDOWN_DELIVERY_DETAIL_TWO_LEGS : TRANSLATIONS.QUOTE_BREAKDOWN_DELIVERY_DETAIL, {
            distanceKm: b.distanceKm.toFixed(1),
            pricePerKm: money(b.pricePerKm),
          })
        : undefined,
      amount: booking.deliveryCost,
    });
  }

  if (booking.insuranceCost != null && booking.insuranceCost > 0) {
    rows.push({
      key: 'insurance',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_LABEL),
      detail: booking.hostInsurancePolicyName ?? booking.insuranceProvider,
      amount: booking.insuranceCost,
    });
  }

  if (booking.accessoriesCost != null && booking.accessoriesCost > 0) {
    rows.push({
      key: 'accessories',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_COST_LABEL),
      detail: booking.accessories?.length ? booking.accessories.map((a) => a.name).join(', ') : undefined,
      amount: booking.accessoriesCost,
    });
  }

  // Guarded: these collections are always present on a current backend,
  // but this sheet renders whatever booking it's handed.
  (booking.serviceLines ?? []).forEach((line) => {
    rows.push({
      key: `service-${line.serviceId}`,
      label: line.title,
      detail: line.providerDisplayName,
      amount: line.amount,
    });
  });

  const history = [...(booking.quoteHistory ?? [])].sort((a, b) => a.versionNumber - b.versionNumber);
  const currentAmount = booking.quoteCurrentAmount ?? booking.totalPrice;
  const hasActions = !!onAccept || !!onPropose || !!onReject || !!onCancel || !!onPay;

  // Whose move it is, said out loud. Without this the absence of an Accept
  // button is indistinguishable from a bug -- which is exactly how it read
  // to a guest opening their own pending quote.
  //
  // Scoped to a viewer with no move of their own: a host who has just
  // counter-offered also can't accept (they proposed the live version --
  // see quoteHelper), but they can still counter again or reject, so
  // nothing about their screen is waiting on anybody, and this copy is
  // written from the guest's side of the deal.
  const awaitingReply = booking.status === 'AwaitingQuote' && !onAccept && !onPropose;

  // The deadline belongs in here too, and in every state -- not only while
  // somebody is waiting. This sheet is where the decision is actually taken,
  // so the pressure that decision is under went missing at precisely the
  // moment it mattered: it used to appear only when the viewer had no move to
  // make, and vanish the moment it became their turn.
  const countdown: { deadlineUtc: string; remainingSeconds?: number; variant: QuoteCountdownVariant } | undefined =
    booking.status === 'AwaitingPayment' && booking.paymentRequiredByUtc
      ? { deadlineUtc: booking.paymentRequiredByUtc, remainingSeconds: booking.paymentRequiredInSeconds, variant: 'pay' }
      : booking.status === 'AwaitingQuote' && booking.quoteRespondByUtc
        ? {
            deadlineUtc: booking.quoteRespondByUtc,
            remainingSeconds: booking.quoteRespondInSeconds,
            // The clock changes hands with each counter-offer, so who it runs
            // against is exactly "can this viewer act" -- the same question
            // awaitingReply already answers.
            variant: awaitingReply ? 'theirs' : 'yours',
          }
        : undefined;

  const body = (
    <div>
      <div style={{ background: SURFACE_1_SOFT, borderRadius: 16, padding: 14, marginBottom: 14 }}>
        <Text strong style={{ display: 'block', fontSize: 15 }}>
          {booking.heavyDutyMachineryTitle}
        </Text>
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 2 }}>
          {datesLabel}
        </Text>
        {booking.guestDisplayName && (
          <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
            {booking.guestDisplayName}
          </Text>
        )}
      </div>

      {awaitingReply && (
        <div
          style={{
            background: TEAL_SOFT_BG,
            border: `1px solid ${TEAL}`,
            borderRadius: 16,
            padding: 12,
            marginBottom: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Text strong style={{ fontSize: 14 }}>
            {translate(t, TRANSLATIONS.QUOTE_AWAITING_HOST_TITLE)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {translate(t, TRANSLATIONS.QUOTE_AWAITING_HOST_DESCRIPTION)}
          </Text>
        </div>
      )}

      {/* Its own card rather than nested inside the banner above: the deadline
          outlives that one state, and a card inside a card reads as clutter. */}
      {countdown && (
        <div style={{ marginBottom: 14 }}>
          <QuoteCountdown {...countdown} />
        </div>
      )}

      {(revisionRows ?? rows).map((row) => (
        <div key={row.key} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <Text style={{ fontSize: 14 }}>{row.label}</Text>
            <Text style={{ fontSize: 14, whiteSpace: 'nowrap' }}>{money(row.amount)}</Text>
          </div>
          {row.detail && (
            <Text type="secondary" style={{ fontSize: 12, opacity: 0.8 }}>
              {row.detail}
            </Text>
          )}
        </div>
      ))}

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <Text strong style={{ fontSize: 15 }}>
          {/* Once the quote is agreed this figure stops being a proposal and
              becomes the bill -- saying "proposed total" over a Pay button
              would undersell what the tap actually does. */}
          {translate(t, onPay ? TRANSLATIONS.QUOTE_BREAKDOWN_AMOUNT_TO_PAY : TRANSLATIONS.QUOTE_BREAKDOWN_CURRENT_TOTAL)}
        </Text>
        <Text strong style={{ fontSize: 20, color: TEAL, whiteSpace: 'nowrap' }}>
          {money(currentAmount)}
        </Text>
      </div>

      {/* The deposit is held, not charged, so it never joins the sum above --
          but a host deciding on a quote still needs to know it exists. */}
      {booking.depositAmount != null && booking.depositAmount > 0 && (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_DEPOSIT_NOTE, { amount: money(booking.depositAmount) })}
          </Text>
          {/* Said HERE, before anybody commits to anything, because it is the
              one thing about the fianza that surprises people later: the hold
              lets go by itself after a few days, and on a long hire that is
              well before the machine comes back. A customer told now reads a
              damage charge at return as the terms they agreed to; the same
              customer told nothing reads it as a charge out of nowhere. */}
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
            {translate(t, TRANSLATIONS.QUOTE_DEPOSIT_HOLD_NOTICE)}
          </Text>
        </>
      )}

      {history.length > 0 && (
        <>
          <Divider style={{ margin: '16px 0 12px' }} />
          <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
            {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_HISTORY_TITLE)}
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map((revision) => {
              // Only meaningful from v2 on -- see the label below.
              const mine = revision.proposedByUserId === userId;
              const isCurrent = revision.versionNumber === history[history.length - 1].versionNumber;
              return (
                <div
                  key={revision.versionNumber}
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    background: isCurrent ? TEAL_SOFT_BG : token.colorFillQuaternary,
                    border: `1px solid ${isCurrent ? TEAL : 'transparent'}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {/* v1 is nobody's proposal: it's the figure the checkout
                          itself computed from the dates and options the guest
                          picked. Attributing it to a person ("you proposed
                          this" / "they proposed this") misdescribes what
                          happened on both sides -- the guest didn't name a
                          price, and neither did the host. Only v2+ are real
                          human counter-offers, and only those get a face. */}
                      {revision.versionNumber === 1
                        ? translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_AUTOMATIC)
                        : translate(t, mine ? TRANSLATIONS.QUOTE_BREAKDOWN_PROPOSED_BY_ME : TRANSLATIONS.QUOTE_BREAKDOWN_PROPOSED_BY_THEM)}
                      {' · '}
                      {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_VERSION, { version: revision.versionNumber })}
                    </Text>
                    <Text strong style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                      {money(revision.amount)}
                    </Text>
                  </div>
                  {/* v1 carries no reason -- there was nothing to explain yet. */}
                  {revision.reason && (
                    <Text style={{ fontSize: 13, display: 'block', marginTop: 3 }}>{revision.reason}</Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 3 }}>
                    {formatShortDateTime(revision.createdAt, i18n.language)}
                  </Text>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  // Stacked full-width rather than a row of three: on a phone these are
  // three very different consequences and a row of small buttons invites
  // hitting the wrong one. Accept first, reject last.
  const actions = hasActions ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {onPay && (
        <Button
          block
          type="primary"
          size="large"
          onClick={onPay}
          style={{ borderRadius: 999, background: TEAL, borderColor: TEAL }}
        >
          {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_PAY_BUTTON)}
        </Button>
      )}
      {onAccept && (
        <Button
          block
          type="primary"
          size="large"
          loading={accepting}
          onClick={onAccept}
          style={{ borderRadius: 999, background: TEAL, borderColor: TEAL }}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_BUTTON)}
        </Button>
      )}
      {onPropose && (
        <Button block size="large" onClick={onPropose} style={{ borderRadius: 999 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_PROPOSE_BUTTON)}
        </Button>
      )}
      {onReject && (
        <Button block danger size="large" onClick={onReject} style={{ borderRadius: 999 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_REJECT_BUTTON)}
        </Button>
      )}
      {onCancel && (
        <Button block danger size="large" loading={cancelling} onClick={onCancel} style={{ borderRadius: 999 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_BUTTON)}
        </Button>
      )}
    </div>
  ) : null;

  const title = translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_TITLE);

  // Same desktop-Modal / mobile-bottom-sheet split as
  // HandoverAcknowledgementSheet -- see its doc comment. The drag handle is
  // the only draggable part: the body scrolls, so making the whole sheet
  // draggable would fight the scroll.
  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="quote-breakdown-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)' }}
            />
            <motion.div
              key="quote-breakdown-sheet"
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              dragSnapToOrigin
              onDragEnd={(_, info) => {
                if (info.offset.y > 80 || info.velocity.y > 500) onClose();
              }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2101,
                width: '100%',
                maxWidth: 480,
                margin: '0 auto',
                background: token.colorBgContainer,
                borderRadius: '20px 20px 0 0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '90vh',
              }}
            >
              <div style={{ padding: '10px 20px 0', flexShrink: 0 }}>
                <div
                  onPointerDown={(e) => dragControls.start(e)}
                  style={{
                    width: 36,
                    height: 4,
                    borderRadius: 2,
                    background: token.colorBorder,
                    margin: '0 auto 14px',
                    cursor: 'grab',
                    touchAction: 'none',
                  }}
                />
                <Title level={5} style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconFileInvoice size={18} />
                  {title}
                </Title>
              </div>
              {/* Only the breakdown scrolls; the decisions stay pinned, so a
                  long quote never hides them below the fold. */}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 20px' }}>{body}</div>
              {actions && (
                <div
                  style={{
                    flexShrink: 0,
                    padding: '12px 20px calc(16px + env(safe-area-inset-bottom, 0px))',
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  {actions}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconFileInvoice size={18} />
          {title}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={actions}
      width={520}
    >
      {body}
    </Modal>
  );
};

export default QuoteBreakdownSheet;
